import { Link } from 'react-router-dom';
import { Palette, Sparkles, Users, Calendar, ArrowRight } from 'lucide-react';

export default function HomePage() {
  const features = [
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Creative Learning",
      description: "Explore painting, drawing, and mixed media techniques in a fun environment.",
      color: "bg-blue-600"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Small Classes",
      description: "Maximum 20 children per class for personalized attention.",
      color: "bg-pink-500"
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Art Pack Included",
      description: "Quality starter art pack included with your term enrollment.",
      color: "bg-yellow-500"
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Term Time Classes",
      description: "Structured term with an end-of-term art exhibition.",
      color: "bg-red-500"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#FDF6E8] py-16 lg:py-24">
        {/* Paint Splash Decorations */}
        <div className="absolute top-10 left-10 w-32 h-32 bg-pink-300 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-blue-300 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-yellow-300 rounded-full opacity-20 blur-2xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="font-heading text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
                Where <span className="text-blue-600">Creativity</span> Comes to <span className="text-pink-500">Life!</span>
              </h1>
              <p className="font-body text-lg text-slate-600 mb-8 max-w-lg">
                After-school art club for primary school children Years 1-6. 
                Watch your child's imagination flourish through painting, drawing, and creative exploration.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/enroll" 
                  className="btn-primary flex items-center gap-2"
                  data-testid="hero-register-btn"
                >
                  Enroll Now <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/art-club" 
                  className="btn-outline"
                  data-testid="hero-learn-more-btn"
                >
                  Learn More
                </Link>
              </div>
            </div>

            <div className="relative animate-fade-in-up animate-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-yellow-400 rounded-3xl transform rotate-3 border-2 border-slate-900"></div>
                <img 
                  src="https://images.unsplash.com/photo-1769720205501-6da2da716884?w=600&h=500&fit=crop"
                  alt="Children painting in art class"
                  className="relative rounded-3xl border-2 border-slate-900 shadow-sticker-xl w-full h-[400px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 border-2 border-slate-900 shadow-sticker">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {[1,2,3].map(i => (
                        <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400 border-2 border-white"></div>
                      ))}
                    </div>
                    <span className="font-heading font-bold text-slate-900 text-lg">200+ Happy Artists!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Why Choose <span className="text-blue-600">Artsy Craftsy</span>?
            </h2>
            <p className="font-body text-lg text-slate-600 max-w-2xl mx-auto">
              We provide a nurturing environment where children can express themselves and develop artistic skills.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="card-sticker group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center text-white mb-4 border-2 border-slate-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] group-hover:rotate-6 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="font-body text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">
            Ready to Unleash Your Child's Creativity?
          </h2>
          <p className="font-body text-xl text-blue-100 mb-8">
            Join hundreds of happy families who have discovered the joy of art with Artsy Craftsy.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/enroll" 
              className="bg-white text-blue-600 font-bold py-3 px-8 rounded-full border-2 border-slate-900 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              data-testid="cta-register"
            >
              Get Started Today
            </Link>
            <Link 
              to="/contact" 
              className="bg-transparent text-white font-bold py-3 px-8 rounded-full border-2 border-white hover:bg-white/10 transition-all"
              data-testid="cta-contact"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
